import { useState, useCallback, useEffect, useRef } from 'react';
import chatApi from '../../services/chatApi';
import * as Sentry from '@sentry/react';

/**
 * Custom hook for managing chat sessions
 */
export const useChatSessions = ({ 
    autoRefresh = true, 
    refreshInterval = 30000, // 30 seconds
    refreshOnFocus = true 
} = {}) => {
    const [sessions, setSessions] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [sessionsError, setSessionsError] = useState(null);
    const [totalSessionsCount, setTotalSessionsCount] = useState(0);
    const [hasMoreSessions, setHasMoreSessions] = useState(true);
    
    // Refs for managing intervals and preventing memory leaks
    const refreshIntervalRef = useRef(null);
    const lastRefreshTime = useRef(Date.now());

    /**
     * Load user's chat sessions
     * @param {number} limit - Number of sessions to load
     * @param {number} offset - Offset for pagination
     * @returns {Promise<Array>} Sessions data
     */
    const loadSessions = useCallback(async (limit = 50, offset = 0) => {
        try {
            setIsLoadingSessions(true);
            setSessionsError(null);

            const response = await chatApi.getSessions(limit, offset);
            
            setTotalSessionsCount(response.total_count);
            setHasMoreSessions(response.sessions.length === limit && (offset + response.sessions.length) < response.total_count);

            if (offset === 0) {
                // Replace sessions if loading from beginning
                setSessions(response.sessions);
            } else {
                // Append if loading more (pagination)
                setSessions(prev => [...prev, ...response.sessions]);
            }

            return response.sessions;

        } catch (error) {
            console.error('Failed to load chat sessions:', error);
            setSessionsError(error.message);
            
            Sentry.captureException(error, { 
                tags: { context: 'chat-sessions-loading' },
                extra: { limit, offset }
            });

            return [];
        } finally {
            setIsLoadingSessions(false);
        }
    }, []);

    /**
     * Load more sessions (for pagination)
     * @param {number} limit - Number of sessions to load
     * @returns {Promise<Array>} New sessions
     */
    const loadMoreSessions = useCallback(async (limit = 20) => {
        if (!hasMoreSessions || isLoadingSessions) {
            return [];
        }

        return await loadSessions(limit, sessions.length);
    }, [hasMoreSessions, isLoadingSessions, sessions.length, loadSessions]);

    /**
     * Refresh sessions list
     */
    const refreshSessions = useCallback(async () => {
        return await loadSessions();
    }, [loadSessions]);

    /**
     * Find session by ID
     * @param {string} sessionId - Session ID to find
     * @returns {Object|null} Session object or null
     */
    const findSessionById = useCallback((sessionId) => {
        return sessions.find(session => session.session_id === sessionId) || null;
    }, [sessions]);

    /**
     * Reset sessions state
     */
    const resetSessionsState = useCallback(() => {
        setSessions([]);
        setSessionsError(null);
        setTotalSessionsCount(0);
        setHasMoreSessions(true);
    }, []);

    /**
     * Get session stats
     */
    const getSessionsStats = useCallback(() => {
        const activeSessions = sessions.filter(s => s.is_active);
        const totalMessages = sessions.reduce((sum, s) => sum + s.message_count, 0);
        
        return {
            totalSessions: sessions.length,
            activeSessions: activeSessions.length,
            totalMessages,
            hasAnySessions: sessions.length > 0
        };
    }, [sessions]);

    /**
     * Silent refresh that doesn't show loading state for auto-updates
     */
    const silentRefresh = useCallback(async () => {
        // Only refresh if not currently loading and enough time has passed
        if (isLoadingSessions || Date.now() - lastRefreshTime.current < 5000) {
            return;
        }

        try {
            lastRefreshTime.current = Date.now();
            const response = await chatApi.getSessions(50, 0);
            
            setTotalSessionsCount(response.total_count);
            setHasMoreSessions(response.sessions.length === 50 && response.sessions.length < response.total_count);
            setSessions(response.sessions);
            setSessionsError(null);

        } catch (error) {
            console.error('Silent refresh failed:', error);
            // Don't update error state for silent refreshes to avoid UI flashing
        }
    }, [isLoadingSessions]);

    /**
     * Start auto-refresh timer
     */
    const startAutoRefresh = useCallback(() => {
        if (!autoRefresh || refreshIntervalRef.current) {
            return;
        }

        refreshIntervalRef.current = setInterval(() => {
            silentRefresh();
        }, refreshInterval);
    }, [autoRefresh, refreshInterval, silentRefresh]);

    /**
     * Stop auto-refresh timer
     */
    const stopAutoRefresh = useCallback(() => {
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }
    }, []);

    /**
     * Handle window focus for refresh on focus
     */
    const handleWindowFocus = useCallback(() => {
        if (refreshOnFocus && Date.now() - lastRefreshTime.current > 5000) {
            silentRefresh();
        }
    }, [refreshOnFocus, silentRefresh]);

    // Effect for auto-refresh setup
    useEffect(() => {
        if (autoRefresh) {
            startAutoRefresh();
        }

        return () => {
            stopAutoRefresh();
        };
    }, [autoRefresh, startAutoRefresh, stopAutoRefresh]);

    // Effect for window focus refresh
    useEffect(() => {
        if (refreshOnFocus) {
            window.addEventListener('focus', handleWindowFocus);
            window.addEventListener('visibilitychange', () => {
                if (!document.hidden) {
                    handleWindowFocus();
                }
            });

            return () => {
                window.removeEventListener('focus', handleWindowFocus);
                window.removeEventListener('visibilitychange', handleWindowFocus);
            };
        }
    }, [refreshOnFocus, handleWindowFocus]);

    return {
        // State
        sessions,
        isLoadingSessions,
        sessionsError,
        totalSessionsCount,
        hasMoreSessions,

        // Actions
        loadSessions,
        loadMoreSessions,
        refreshSessions,
        findSessionById,
        resetSessionsState,
        silentRefresh,
        startAutoRefresh,
        stopAutoRefresh,

        // Computed values
        hasSessionsData: sessions.length > 0,
        sessionsStats: getSessionsStats(),
    };
}; 